import axios from 'axios';
import { RefreshAccessToken } from './Auth';

const apiUrl = process.env.REACT_APP_API_URL;

// Shared in-flight refresh promise: when multiple requests fail with an
// expired access token at the same time (e.g. multiple tabs, parallel calls
// on a page), they all wait on a single /refresh-token call instead of
// stampeding the endpoint.
let inflightRefresh = null;
function refreshOnce() {
  if (!inflightRefresh) {
    inflightRefresh = RefreshAccessToken().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

function redirectToLogin() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined' && window.location) {
    window.location.assign('/login');
  }
}

function isTokenExpired(error) {
  return (
    error?.response?.status === 401 &&
    error.response?.data?.name === 'TokenExpiredError'
  );
}

function withAuthHeader(config, token) {
  const base = config?.headers ?? {};
  return token ? { ...config, headers: { ...base, Authorization: `Bearer ${token}` } } : { ...config, headers: { ...base } };
}

async function callAxios(method, fullUrl, data, config) {
  if (data === undefined) {
    return axios[method](fullUrl, config);
  }
  return axios[method](fullUrl, data, config);
}

async function request(method, url, data, userConfig = {}) {
  const fullUrl = `${apiUrl}${url}`;
  const token = localStorage.getItem('accessToken');
  const config = withAuthHeader(userConfig, token);

  try {
    return await callAxios(method, fullUrl, data, config);
  } catch (error) {
    if (!isTokenExpired(error) || userConfig._retried) {
      throw error;
    }
    let newAccessToken;
    try {
      newAccessToken = await refreshOnce();
    } catch (refreshError) {
      redirectToLogin();
      throw refreshError;
    }
    const retryConfig = withAuthHeader({ ...userConfig, _retried: true }, newAccessToken);
    return callAxios(method, fullUrl, data, retryConfig);
  }
}

const apiClient = {
  get: (url, config) => request('get', url, undefined, config),
  delete: (url, config) => request('delete', url, undefined, config),
  post: (url, data, config) => request('post', url, data, config),
  put: (url, data, config) => request('put', url, data, config),
  patch: (url, data, config) => request('patch', url, data, config),
};

export default apiClient;
